import CloseIcon from "@mui/icons-material/Close";
import { Grid, TextField, Typography } from "@mui/material";
import { useCallback } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { returnEmptyStringIfNullOrUndefined } from "../../../api/aml/utils";
import { TableMenuItems } from "../../../shared/constants/common.constant";
import { whiteSpaceRegExpression } from "../../../shared/constants/pattern.constant";
import { MLButton } from "../../../shared/styles/common-styles";
import type { Profile } from "../types/profile.type";
import { usePermissionMap } from "../../../hooks/usePermissions";

const SelectionStatus = styled.span`
  display: flex;
  align-items: center;

  .clear-selection {
    display: flex;
    margin: 0 10px;
    cursor: pointer;

    :hover {
      text-decoration: underline;
    }
  }
`;

const SearchBoxWrapper = styled(Grid)`
  .search-text input {
    height: 1.5rem;
  }
  .search-text p {
    position: absolute;
    bottom: -15px;
    left: -10px;
  }
`;

interface ProfileSearchProps<TField extends FieldValues> {
  selectedProfiles: Array<Profile>;
  control: Control<TField>;
  handleSearch: () => void;
  handleReset: () => void;
  handleClear: () => void;
  onBulkAction: (_actionItem: string) => void;
}

const ProfilesListSearch = <TField extends FieldValues>({
  control,
  selectedProfiles,
  handleSearch,
  handleReset,
  handleClear,
  onBulkAction,
}: ProfileSearchProps<TField>): JSX.Element => {
  const { t: translate } = useTranslation(["profile", "common"]);

  const permissions = usePermissionMap();

  const handleBulkDelete = useCallback(() => {
    onBulkAction(TableMenuItems.delete);
  }, [onBulkAction]);

  const handleBulkAssign = useCallback(() => {
    onBulkAction(TableMenuItems.assign);
  }, [onBulkAction]);

  const profileSearchValidationSchema = {
    required: translate("profiles.searchInputError"),
    maxLength: {
      value: 150,
      message: translate("profiles.searchInputError"),
    },
    pattern: {
      value: whiteSpaceRegExpression,
      message: translate("profiles.searchInputError"),
    },
  };

  return (
    <Grid container sx={{ display: "flex", py: 2, marginBottom: 0.6 }}>
      <SearchBoxWrapper
        item
        xs={6}
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Controller
          name={"searchBoxInput" as Path<TField>}
          control={control}
          rules={{ ...profileSearchValidationSchema }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              className="search-text"
              size="small"
              variant="outlined"
              placeholder={
                returnEmptyStringIfNullOrUndefined(
                  translate("profiles.searchQuery")
                ) ?? ""
              }
              inputProps={{
                "aria-label": "search input box",
                "data-private": "true",
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
          )}
        />
        <MLButton
          onClick={handleSearch}
          type="submit"
          variant="primary"
          aria-label="search button"
        >
          {translate("common:search")}
        </MLButton>
        <MLButton
          onClick={handleReset}
          type="button"
          variant="grey"
          aria-label="reset button"
        >
          {translate("common:reset")}
        </MLButton>
      </SearchBoxWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {selectedProfiles.length > 0 && (
          <SelectionStatus>
            <Typography variant={"body1"} aria-label="selected profiles">
              {translate("profiles.selectedProfiles", {
                count: selectedProfiles.length,
              })}
            </Typography>
            <Typography
              variant={"body1"}
              onClick={handleClear}
              className="clear-selection"
              aria-label="clear selection"
              alignItems={"center"}
            >
              <CloseIcon fontSize="small" style={{ marginBottom: "1px" }} />
              {translate("common:clear")}
            </Typography>
          </SelectionStatus>
        )}
        {permissions.DeleteProfile && (
          <MLButton
            onClick={handleBulkDelete}
            type="button"
            variant="primary-outline"
            disabled={!selectedProfiles.length}
            aria-label="Delete button"
          >
            {translate("common:delete")}
          </MLButton>
        )}
        <MLButton
          onClick={handleBulkAssign}
          type="button"
          variant="grey"
          disabled={!selectedProfiles.length}
          aria-label="Assign button"
        >
          {translate("common:assign")}
        </MLButton>
      </Grid>
    </Grid>
  );
};

export default ProfilesListSearch;
